import { createContext, ReactNode, useMemo, useState } from "react";
import { Helpers } from "../helpers/helpers";
import { useLocalStorage } from "../helpers/useLocalStorage";
import { useAnalytics } from "../firebase/firebase";
import { ChooseFavouritesFolderModal } from "../modal/chooseFavouritesFolderModal";
import { IQuestion } from "../types";
import { EditFolderModal } from "../modal/editFolderModal";

export interface IFavouritesFolder {
    items: IQuestion[];
    name: string;
    id: number;
}

interface IFavourites {
    updateFolderName: (id: number, name: string) => void;
    isFavourite: (id: string) => boolean;
    showAddToFavouritesModal: (qns: IQuestion[]) => void;
    showEditFolderModal: (folder: IFavouritesFolder) => void;
    removeFromAllFavourites: (id: string) => void;
    deleteFolder: (id: number) => void;
    createFolder: (name: string) => void;
    // outOf: number;
    folders: IFavouritesFolder[];
}

interface FavouritesProviderProps {
    children: ReactNode;
}

export const FavouritesContext = createContext<IFavourites | undefined>(undefined);

export const FavouritesProvider = (props: FavouritesProviderProps) => {
    const { track } = useAnalytics();
    const [folders, setFolders] = useLocalStorage<IFavouritesFolder[]>("favourites.items2", []);


    // ADD TO FAVOURITES
    const [addFavouritesOpen, setAddFavouritesOpen] = useState<boolean>(false);
    const [questionsToAdd, setQuestionsToAdd] = useState<IQuestion[]>([]);

    // EDIT FAVOURITES FOLDER
    const [editFolderOpen, setEditFolderOpen] = useState<boolean>(false);
    const [editFolder, setEditFolder] = useState<IFavouritesFolder | null>(null);

    // const [selectedItemsOutOfValue, setselectedItemsOutOfValue] = useState<number>(0);
    // useEffect(() => {
    //     setselectedItemsOutOfValue(Helpers.sum(foldersState.map(i => i.num_marks)));
    // }, [foldersState.length])

    const isFavouriteMemo = useMemo(() => {
        return (id: string) => folders.map(f => f.items).flat().find(q => q.id == id) != undefined;
    }, [folders])

    return (
        <FavouritesContext value={{
            showAddToFavouritesModal: (questions: IQuestion[]) => {
                setQuestionsToAdd(questions);
                setAddFavouritesOpen(true);
            },
            showEditFolderModal: (folder: IFavouritesFolder) => {
                setEditFolder(folder);
                setEditFolderOpen(true);
            },
            removeFromAllFavourites: (id: string) => {
                for (let folder of folders) {
                    folder.items = folder.items.filter(q => q.id != id);
                }
                setFolders([...folders]);
                track("favourites.remove", { "id": id });
            },
            deleteFolder: (id: number) => {
                setFolders([...folders.filter(f => f.id != id)]);
                track("favourites.clear", {});
            },
            isFavourite: isFavouriteMemo,
            updateFolderName: (id: number, name: string) => {
                var folder = folders.find(f => f.id == id);
                if (!folder) return;
                folder.name = name;
            },
            folders,
            createFolder: (name: string) => {
                let newFolder = {
                    items: [],
                    name,
                    id: Helpers.randomInt(1e10),
                };
                setFolders([...folders, newFolder]);
            },
            // outOf: selectedItemsOutOfValue
        }}>
            {props.children}
            <ChooseFavouritesFolderModal
                open={addFavouritesOpen}
                setOpen={setAddFavouritesOpen}
                chooseFolderHandler={(id: number) => {
                    var folder = folders.find(f => f.id == id);
                    if (!folder) return;
                    for (var q of questionsToAdd) {
                        if (!folder.items.find(i => i.id == q.id)) {
                            folder.items.push(q);
                        }
                    }
                    setFolders([...folders]);
                    track("favourites.add", {
                        items: questionsToAdd.map(q => { return { "id": q.id, "collection": q.collection } })
                    });
                }} />


            <EditFolderModal
                open={editFolderOpen}
                setOpen={setEditFolderOpen}
                submitHandler={(newName: string) => {
                    if (editFolder) {
                        editFolder.name = newName;
                        setFolders([...folders]);
                        track("favourites.rename", {
                            folderId: editFolder?.id
                        });
                    }
                }}
                oldName={editFolder?.name ?? ""} />
        </FavouritesContext>
    )

}